import React, { useState, useEffect, useMemo } from "react";
import {
  useGetAllTenantsQuery,
  useGetUpdateMutation,
  useCreateProxyAdminMutation,
  useLazyGetProxyAdminsDrilldownQuery,
  useRevokeProxyAdminMutation,
} from "../../api/tenantApi";
import EditIcon from "@mui/icons-material/Edit";
import TextField from "../customComponents/customTextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { Snackbar, Alert } from "@mui/material";
import { CustomPhoneInput } from "./TenantForm";
import { validateMobileNumber } from "../../utils";

const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const emptyNewAdmin = () => ({ name: "", emailId: "", phoneNumber: "" });
const emptyNewAdminError = () => ({ name: "", emailId: "", phoneNumber: "" });

// Generate consistent avatar color from string
const avatarColor = (str = "") => {
  const palette = [
    "bg-blue-primary",
    "bg-indigo-500",
    "bg-violet-500",
    "bg-rose-500",
    "bg-amber-500",
    "bg-emerald-500",
    "bg-teal-500",
    "bg-cyan-500",
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
};

const initialsOf = (name = "") => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const ManageTenants = () => {
  const [tenantData, setTenantData] = useState([]);
  const [getUpdate] = useGetUpdateMutation();
  const [createProxyAdmin] = useCreateProxyAdminMutation();
  const [revokeProxyAdmin] = useRevokeProxyAdminMutation();
  const [fetchProxyAdmins, { isFetching: drilldownLoading }] = useLazyGetProxyAdminsDrilldownQuery();

  const { data, isError, refetch: isRefetchTenants } = useGetAllTenantsQuery();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [activeTab, setActiveTab] = useState("details");

  // Existing proxy admins (fetched from drilldown)
  const [existingProxyAdmins, setExistingProxyAdmins] = useState([]);
  // New proxy admins to be added
  const [newAdmins, setNewAdmins] = useState([]);
  const [newAdminErrors, setNewAdminErrors] = useState([]);

  const [snackbar, setSnackbar] = useState({ open: false, error: false, message: "" });

  // Revoke confirmation
  const [revokeConfirm, setRevokeConfirm] = useState({ open: false, admin: null });
  const [revoking, setRevoking] = useState(false);

  // Search & filter
  const [search, setSearch] = useState("");

  // Per-tenant proxy admin previews fetched on load
  const [adminsByTenant, setAdminsByTenant] = useState({}); // { [tenantId]: { loading, items } }

  useEffect(() => {
    isRefetchTenants();
  }, []);

  useEffect(() => {
    if (data) {
      const dataWithId = data.map((row, index) => ({ ...row, id: index + 1 }));
      setTenantData(dataWithId);
    }
  }, [data]);

  useEffect(() => {
    if (isError) setTenantData([]);
  }, [isError]);

  // Fetch proxy admins for every tenant whenever tenants change
  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) return;
    let cancelled = false;
    const initial = {};
    data.forEach((t) => { if (t?.tenantId) initial[t.tenantId] = { loading: true, items: [] }; });
    setAdminsByTenant(initial);

    (async () => {
      const results = await Promise.allSettled(
        data.map((t) =>
          t?.tenantId
            ? fetchProxyAdmins({ tenantId: t.tenantId, employeeId: t._id })
            : Promise.resolve({ data: [] })
        )
      );
      if (cancelled) return;
      const next = {};
      data.forEach((t, idx) => {
        if (!t?.tenantId) return;
        const r = results[idx];
        const items = r?.status === "fulfilled" ? (Array.isArray(r.value?.data) ? r.value.data : []) : [];
        next[t.tenantId] = { loading: false, items };
      });
      setAdminsByTenant(next);
    })();

    return () => { cancelled = true; };
  }, [data, fetchProxyAdmins]);

  // Resolve both IDs from the raw data for the selected tenant
  let resolvedTenantId;
  let resolvedEmployeeId;
  data?.forEach((item) => {
    if (item?.tenantName === selectedRowData?.tenantName) {
      resolvedTenantId = item.tenantId;
      resolvedEmployeeId = item._id;
    }
  });

  const handleEdit = (rowData) => {
    setSelectedRowData(rowData);
    setNewAdmins([]);
    setNewAdminErrors([]);
    setExistingProxyAdmins([]);
    setActiveTab("details");
    setOpenDialog(true);
  };

const handleViewAdmins = (rowData) => {
  setSelectedRowData(rowData);
  setNewAdmins([]);
  setNewAdminErrors([]);
  setExistingProxyAdmins([]);
  setActiveTab("proxyAdmins");
  setOpenDialog(true);

  // pass rowData directly
  handleTabChange("proxyAdmins", rowData);
};

const handleTabChange = async (tab, rowDataParam = selectedRowData) => {
  setActiveTab(tab);
  if (tab === "proxyAdmins" && rowDataParam) {
    try {
      const tenant = data?.find(
        (item) => item?.tenantName === rowDataParam?.tenantName
      );

      const result = await fetchProxyAdmins({
        tenantId: tenant?.tenantId,
        employeeId: tenant?._id,
      });
      setExistingProxyAdmins(
        Array.isArray(result?.data?.data)
          ? result.data.data
          : Array.isArray(result?.data)
          ? result.data
          : []
      );
    } catch {
      setExistingProxyAdmins([]);
    }
  }
};

  const handleUrlChange = (e) => {
    setSelectedRowData((prev) => ({ ...prev, logoUrl: e.target.value }));
  };

  // ── New admin handlers ────────────────────────────────────────────────────
  const handleNewAdminChange = (index, field, value) => {
    setNewAdmins((prev) =>
      prev.map((a, i) => (i === index ? { ...a, [field]: value } : a))
    );
  };

  const handleNewAdminBlur = (index, field, value) => {
    setNewAdminErrors((prev) =>
      prev.map((err, i) => {
        if (i !== index) return err;
        const newErr = { ...err };
        if (!value.trim()) {
          if (field === "name") newErr.name = "Please enter admin name";
          else if (field === "emailId") newErr.emailId = "Please enter admin email";
          else if (field === "phoneNumber") newErr.phoneNumber = "Please enter admin mobile";
        } else if (field === "emailId" && !emailPattern.test(value)) {
          newErr.emailId = "Please enter a valid email address";
        } else {
          newErr[field] = "";
        }
        return newErr;
      })
    );
  };

  const handleNewAdminMobileBlur = (index, value) => {
    setNewAdminErrors((prev) =>
      prev.map((err, i) => {
        if (i !== index) return err;
        const newErr = { ...err };
        if (!value) newErr.phoneNumber = "Please enter admin mobile";
        else if (!validateMobileNumber(value)) newErr.phoneNumber = "Please enter a valid mobile number";
        else newErr.phoneNumber = "";
        return newErr;
      })
    );
  };

  const addNewAdminRow = () => {
    setNewAdmins((prev) => [...prev, emptyNewAdmin()]);
    setNewAdminErrors((prev) => [...prev, emptyNewAdminError()]);
  };

  const removeNewAdminRow = (index) => {
    setNewAdmins((prev) => prev.filter((_, i) => i !== index));
    setNewAdminErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const isNewAdminsValid = () =>
    newAdmins.length > 0 &&
    newAdmins.every(
      (a, i) =>
        a.name.trim() !== "" &&
        a.emailId.trim() !== "" &&
        emailPattern.test(a.emailId) &&
        a.phoneNumber !== "" &&
        (newAdminErrors[i]?.phoneNumber ?? "") === ""
    );

  // ── Save proxy admins via createproxyadmin API ────────────────────────────
  const handleSaveProxyAdmins = async () => {
    try {
      await createProxyAdmin({
        payload: {
          tenantId: resolvedTenantId,
          proxyAdmins: newAdmins,
        },
      });
      setSnackbar({ open: true, error: false, message: "Proxy admins added successfully" });
      const result = await fetchProxyAdmins({
        tenantId: resolvedTenantId,
        employeeId: resolvedEmployeeId,
      });
      const refreshed = Array.isArray(result?.data?.data) ? result.data.data : Array.isArray(result?.data) ? result.data : [];
      setExistingProxyAdmins(refreshed);
      setAdminsByTenant((prev) => ({
        ...prev,
        [resolvedTenantId]: { loading: false, items: refreshed },
      }));
      setNewAdmins([]);
      setNewAdminErrors([]);
    } catch {
      setSnackbar({ open: true, error: true, message: "Failed to add proxy admins" });
    }
  };

  // ── Revoke proxy admin ────────────────────────────────────────────────────
  const handleRevokeConfirm = (admin) => {
    setRevokeConfirm({ open: true, admin });
  };

  const handleRevokeCancel = () => {
    setRevokeConfirm({ open: false, admin: null });
  };

  const handleRevokeExecute = async () => {
    const { admin } = revokeConfirm;
    setRevoking(true);
    setRevokeConfirm({ open: false, admin: null });
    try {
      await revokeProxyAdmin({ tenantId: resolvedTenantId, employeeId: admin.employeeId });
      setSnackbar({ open: true, error: false, message: `${admin.firstName}'s access has been revoked` });
      const result = await fetchProxyAdmins({
        tenantId: resolvedTenantId,
        employeeId: resolvedEmployeeId,
      });
      const refreshed = Array.isArray(result?.data?.data) ? result.data.data : Array.isArray(result?.data) ? result.data : [];
      setExistingProxyAdmins(refreshed);
      setAdminsByTenant((prev) => ({
        ...prev,
        [resolvedTenantId]: { loading: false, items: refreshed },
      }));
    } catch {
      setSnackbar({ open: true, error: true, message: "Failed to revoke proxy admin access" });
    } finally {
      setRevoking(false);
    }
  };

  // ── Update tenant details (logoUrl only) ─────────────────────────────────
  const handleUpdate = async () => {
    setOpenDialog(false);
    await getUpdate({ id: resolvedEmployeeId, payload: { ...selectedRowData, _id: "" } });
    isRefetchTenants();
  };

  // Filtered tenants
  const visibleTenants = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenantData;
    return tenantData.filter((t) => {
      return (
        (t.tenantName || "").toLowerCase().includes(q) ||
        (t.email || "").toLowerCase().includes(q) ||
        (t.description || "").toLowerCase().includes(q) ||
        (t.address || "").toLowerCase().includes(q)
      );
    });
  }, [search, tenantData]);

  // Aggregate stats
  const totalAdminsAcross = useMemo(() => {
    return Object.values(adminsByTenant).reduce(
      (sum, v) => sum + (v?.items?.length || 0),
      0
    );
  }, [adminsByTenant]);

  return (
    <div className="px-8 py-7 bg-blue-secondery-2 min-h-[calc(100vh-64px)]">
      {/* ── Header ── */}
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-secondery-1 text-blue-primary text-[11px] font-bold uppercase tracking-wider mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-primary" />
            Directory
          </div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">All Tenants</h1>
          <p className="text-sm text-gray-primary mt-1 max-w-2xl">
            Manage every organization registered on the platform. Each card shows the tenant profile and the proxy admins authorized to operate the workspace.
          </p>
        </div>
      </div>

      {/* ── Stat strip ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total Tenants" value={tenantData.length} icon="building" />
        {/* <StatCard label="Proxy Admins" value={totalAdminsAcross} icon="shield" /> */}
        {/* <StatCard
          label="With Admins"
          value={Object.values(adminsByTenant).filter((v) => v?.items?.length > 0).length}
          icon="check"
        /> */}
        {/* <StatCard
          label="Pending Setup"
          value={Object.values(adminsByTenant).filter((v) => !v?.loading && (v?.items?.length || 0) === 0).length}
          icon="clock"
        /> */}
      </div>

      {/* ── Search bar ── */}
      <div className="bg-white rounded-2xl border border-blue-secondery shadow-sm p-4 mb-6 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1 relative">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-primary pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
          </span>
          <input
            type="text"
            placeholder="Search tenants by name, email, description, or address…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border border-blue-secondery bg-blue-secondery-2 rounded-xl pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary placeholder:text-gray-primary"
          />
        </div>
        <div className="text-xs text-gray-primary whitespace-nowrap">
          Showing <span className="font-bold text-slate-800">{visibleTenants.length}</span> of <span className="font-bold text-slate-800">{tenantData.length}</span>
        </div>
      </div>

      {/* ── Tenant Cards Grid ── */}
      {tenantData.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-blue-secondery p-12 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-secondery-2 text-blue-primary mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" /></svg>
          </div>
          <p className="text-base font-semibold text-slate-700">No tenants yet</p>
          <p className="text-sm text-gray-primary mt-1">Onboard your first organization from the "Onboard Tenant" tab.</p>
        </div>
      ) : visibleTenants.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-blue-secondery p-12 text-center">
          <p className="text-sm text-gray-primary">No tenants match "<span className="text-slate-700 font-semibold">{search}</span>".</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {visibleTenants.map((tenant) => {
            const adminState = adminsByTenant[tenant.tenantId] || { loading: true, items: [] };
            const admins = adminState.items || [];
            const visibleAdmins = admins.slice(0, 3);
            const overflow = Math.max(0, admins.length - visibleAdmins.length);
            return (
              <div
                key={tenant.id}
                className="group bg-white rounded-2xl border border-blue-secondery shadow-sm hover:shadow-lg hover:border-blue-primary/50 transition-all duration-200 overflow-hidden flex flex-col"
              >
                {/* Card top — identity */}
                <div className="p-5 border-b border-blue-secondery-1 bg-gradient-to-br from-white to-blue-secondery-2">
                  <div className="flex items-start gap-3">
                    {tenant.logoUrl ? (
                      <img
                        src={tenant.logoUrl}
                        alt={tenant.tenantName}
                        className="w-12 h-12 rounded-xl object-cover border border-blue-secondery shadow-sm bg-white"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-base shadow-sm ${avatarColor(tenant.tenantName || "")}`}>
                        {initialsOf(tenant.tenantName)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-base font-bold text-slate-800 truncate" title={tenant.tenantName}>
                          {tenant.tenantName || "Unnamed"}
                        </h3>
                        <button
                          type="button"
                          onClick={() => handleEdit(tenant)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center w-8 h-8 rounded-lg text-blue-primary bg-blue-secondery-2 hover:bg-blue-secondery-1"
                          title="Edit tenant"
                        >
                          <EditIcon fontSize="small" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-primary truncate mt-0.5" title={tenant.description}>
                        {tenant.description || "No description"}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-secondery-2 text-blue-primary text-[10px] font-bold uppercase tracking-wider">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          {tenant.createdDate ? new Date(tenant.createdDate).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card middle — contact details */}
                <div className="px-5 py-4 grid grid-cols-1 gap-2.5 text-sm flex-1">
                  <DetailRow icon="mail" label="Email" value={tenant.email} />
                  <DetailRow icon="phone" label="Phone" value={tenant.phoneNumber} />
                  <DetailRow icon="map" label="Address" value={tenant.address} truncate />
                </div>

                {/* Action footer */}
                <div className="px-5 py-3 bg-white border-t border-blue-secondery-1 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(tenant)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold text-slate-700 bg-blue-secondery-2 border border-blue-secondery rounded-lg px-3 py-2 hover:bg-blue-secondery-1 transition-colors"
                  >
                    <EditIcon fontSize="inherit" /> Edit Tenant
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewAdmins(tenant)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold text-white bg-blue-primary rounded-lg px-3 py-2 hover:bg-blue-500 transition-colors shadow-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    Manage Admins
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Edit / Manage Dialog ── */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle className="!border-b !border-blue-secondery-1 !px-6 !py-4 !bg-blue-secondery-2">
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center justify-center w-11 h-11 rounded-xl text-white font-bold shadow-sm ${avatarColor(selectedRowData?.tenantName || "")}`}>
              {initialsOf(selectedRowData?.tenantName)}
            </span>
            <div className="flex flex-col leading-tight">
              <span className="font-bold text-slate-800 text-lg">
                {selectedRowData?.tenantName || "Tenant"}
              </span>
              <span className="text-xs font-normal text-gray-primary">
                {selectedRowData?.email || ""}
              </span>
            </div>
          </div>
        </DialogTitle>

        {/* Tab bar */}
        <div className="flex border-b border-blue-secondery-1 bg-white px-3 pt-1">
          <button
            className={`px-5 py-3 text-sm font-bold transition-all duration-200 relative ${
              activeTab === "details" ? "text-blue-primary" : "text-gray-primary hover:text-slate-700"
            }`}
            onClick={() => handleTabChange("details")}
          >
            Tenant Details
            {activeTab === "details" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-primary rounded-t-full" />
            )}
          </button>
          <button
            className={`px-5 py-3 text-sm font-bold transition-all duration-200 flex items-center gap-2 relative ${
              activeTab === "proxyAdmins" ? "text-blue-primary" : "text-gray-primary hover:text-slate-700"
            }`}
            onClick={() => handleTabChange("proxyAdmins")}
          >
            Proxy Admins
            {existingProxyAdmins.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-blue-secondery-1 text-blue-primary">
                {existingProxyAdmins.length}
              </span>
            )}
            {activeTab === "proxyAdmins" && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-primary rounded-t-full" />
            )}
          </button>
        </div>

        <DialogContent sx={{ minHeight: 360 }}>
          {/* ── Tenant Details tab ── */}
          {selectedRowData && activeTab === "details" && (
            <div className="flex flex-col gap-1 pt-2">
              <TextField label="Created Date" value={selectedRowData.createdDate ? new Date(selectedRowData.createdDate).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : ""} fullWidth disabled />
              <TextField label="Tenant Name" value={selectedRowData.tenantName} fullWidth disabled />
              <TextField label="Description" value={selectedRowData.description} fullWidth disabled />
              <TextField label="Email" value={selectedRowData.email} fullWidth disabled />
              <TextField label="Phone Number" value={selectedRowData.phoneNumber} fullWidth disabled />
              <TextField label="Logo URL" value={selectedRowData.logoUrl} fullWidth onChange={handleUrlChange} />
              <TextField label="Address" value={selectedRowData.address} fullWidth disabled />
            </div>
          )}

          {/* ── Proxy Admins tab ── */}
          {activeTab === "proxyAdmins" && (
            <div className="pt-2">
              {/* Existing proxy admins */}
              {drilldownLoading || revoking ? (
                <div className="text-sm text-gray-primary py-8 text-center flex items-center justify-center gap-2">
                  <span className="inline-block w-4 h-4 border-2 border-blue-primary border-t-transparent rounded-full animate-spin" />
                  {revoking ? "Revoking access…" : "Loading proxy admins…"}
                </div>
              ) : existingProxyAdmins.length > 0 ? (
                <div className="mb-6">
                  <p className="text-[10px] font-bold text-gray-primary uppercase tracking-wider mb-3 flex items-center gap-2">
                    <span className="w-1 h-4 bg-blue-primary rounded-full"></span>
                    Existing Proxy Admins
                  </p>
                  <div className="flex flex-col gap-2.5">
                    {existingProxyAdmins.map((admin, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 border border-blue-secondery-1 rounded-xl px-4 py-3 bg-blue-secondery-2 hover:bg-white hover:shadow-sm transition-all"
                      >
                        <span className={`w-10 h-10 rounded-full text-white font-bold text-sm flex items-center justify-center shadow-sm shrink-0 ${avatarColor(admin.firstName || admin.personalEmail || "")}`}>
                          {initialsOf(admin.firstName || admin.personalEmail || "")}
                        </span>
                        <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-3 gap-2">
                          <div className="min-w-0">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-primary">Name</p>
                            <p className="font-semibold text-slate-700 truncate">{admin.firstName || "—"}</p>
                          </div>
                          <div className="min-w-0">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-primary">Email</p>
                            <p className="font-medium text-slate-700 truncate" title={admin.personalEmail}>
                              {admin.personalEmail || "—"}
                            </p>
                          </div>
                          <div className="min-w-0">
                            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-primary">Mobile</p>
                            <p className="font-medium text-slate-700 truncate">{admin.mobile || "—"}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRevokeConfirm(admin)}
                          className="text-xs font-bold text-rose-600 bg-white border border-rose-200 rounded-lg px-3 py-1.5 hover:bg-rose-50 hover:border-rose-300 transition-colors whitespace-nowrap shadow-sm shrink-0"
                        >
                          Revoke
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-primary mb-5 py-6 px-4 bg-blue-secondery-2 rounded-xl border border-dashed border-blue-secondery text-center">
                  No proxy admins found for this tenant.
                </div>
              )}

              {/* Add new proxy admins */}
              <div className="flex items-center justify-between mb-3 mt-4">
                <p className="text-[10px] font-bold text-gray-primary uppercase tracking-wider flex items-center gap-2">
                  <span className="w-1 h-4 bg-emerald-500 rounded-full"></span>
                  Add New Proxy Admins
                </p>
                <button
                  type="button"
                  onClick={addNewAdminRow}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-blue-primary rounded-lg px-3 py-2 hover:bg-blue-500 transition-colors shadow-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                  Add
                </button>
              </div>

              {newAdmins.length === 0 ? (
                <p className="text-xs text-gray-primary italic px-4 py-3 bg-blue-secondery-2 rounded-lg border border-dashed border-blue-secondery">Click "Add" to add a new proxy admin.</p>
              ) : (
                <div className="flex flex-col gap-4">
                  {newAdmins.map((admin, index) => (
                    <div
                      key={index}
                      className="border border-blue-secondery rounded-xl p-4 bg-blue-secondery-2"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-bold text-slate-700 inline-flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-primary text-white text-xs">{index + 1}</span>
                          New Admin {index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeNewAdminRow(index)}
                          className="text-xs font-medium text-rose-600 bg-white border border-rose-200 rounded-md px-2.5 py-1 hover:bg-rose-50 hover:border-rose-300 transition-colors"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="flex flex-col gap-3">
                        {/* Name */}
                        <div>
                          <label className="text-[10px] font-bold text-gray-primary uppercase tracking-wider block mb-1.5">
                            Name <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="text"
                            className="border border-blue-secondery rounded-lg px-3.5 py-2.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary bg-white placeholder:text-gray-primary transition-colors"
                            placeholder="Enter admin name"
                            value={admin.name}
                            onChange={(e) => handleNewAdminChange(index, "name", e.target.value)}
                            onBlur={(e) => handleNewAdminBlur(index, "name", e.target.value)}
                          />
                          {newAdminErrors[index]?.name && (
                            <p className="text-rose-500 text-xs mt-1.5 flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-rose-500"></span>{newAdminErrors[index].name}</p>
                          )}
                        </div>

                        {/* Email */}
                        <div>
                          <label className="text-[10px] font-bold text-gray-primary uppercase tracking-wider block mb-1.5">
                            Email <span className="text-rose-500">*</span>
                          </label>
                          <input
                            type="email"
                            className="border border-blue-secondery rounded-lg px-3.5 py-2.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-primary focus:border-blue-primary bg-white placeholder:text-gray-primary transition-colors"
                            placeholder="Enter admin email"
                            value={admin.emailId}
                            onChange={(e) => handleNewAdminChange(index, "emailId", e.target.value)}
                            onBlur={(e) => handleNewAdminBlur(index, "emailId", e.target.value)}
                          />
                          {newAdminErrors[index]?.emailId && (
                            <p className="text-rose-500 text-xs mt-1.5 flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-rose-500"></span>{newAdminErrors[index].emailId}</p>
                          )}
                        </div>

                        {/* Mobile */}
                        <div>
                          <label className="text-[10px] font-bold text-gray-primary uppercase tracking-wider block mb-1.5">
                            Mobile <span className="text-rose-500">*</span>
                          </label>
                          <CustomPhoneInput
                            name={`newAdminMobile_${index}`}
                            value={admin.phoneNumber}
                            onPhoneInputChange={(e) =>
                              handleNewAdminChange(index, "phoneNumber", e.target.value)
                            }
                            customPlaceholder="Enter mobile number"
                            onBlurEvent={(value) => handleNewAdminMobileBlur(index, value)}
                            error={newAdminErrors[index]?.phoneNumber || ""}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions className="!border-t !border-blue-secondery-1 !px-5 !py-3.5 !bg-blue-secondery-2">
          <Button onClick={() => setOpenDialog(false)} variant="outlined" size="small" sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, borderColor: "#BFDBFE", color: "#475569" }}>
            Cancel
          </Button>

          {activeTab === "details" && (
            <Button onClick={handleUpdate} variant="contained" size="small" sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, backgroundColor: "#60A5FA", "&:hover": { backgroundColor: "#3B82F6" } }}>
              Update
            </Button>
          )}

          {activeTab === "proxyAdmins" && (
            <Button
              onClick={handleSaveProxyAdmins}
              variant="contained"
              size="small"
              disabled={!isNewAdminsValid()}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 700,
                backgroundColor: !isNewAdminsValid() ? undefined : "#60A5FA",
                "&:hover": { backgroundColor: "#3B82F6" },
              }}
            >
              Save Proxy Admins
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Revoke confirmation dialog ── */}
      <Dialog open={revokeConfirm.open} onClose={handleRevokeCancel} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle className="!font-bold !text-slate-800 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-rose-100 text-rose-600">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
          </span>
          Revoke Proxy Admin Access
        </DialogTitle>
        <DialogContent>
          {revokeConfirm.admin && (
            <p className="text-sm text-slate-600 leading-relaxed">
              Are you sure you want to revoke access for{" "}
              <span className="font-bold text-slate-800">{revokeConfirm.admin.firstName}</span>
              {" "}<span className="text-gray-primary">({revokeConfirm.admin.companyEmail})</span>?
              <br />
              <span className="text-rose-600 font-medium">This action cannot be undone.</span>
            </p>
          )}
        </DialogContent>
        <DialogActions className="!px-5 !pb-4">
          <Button onClick={handleRevokeCancel} variant="outlined" size="small" sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleRevokeExecute}
            variant="contained"
            color="error"
            size="small"
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700 }}
          >
            Revoke Access
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, error: false, message: "" })}
      >
        <Alert
          severity={snackbar.error ? "error" : "success"}
          variant="filled"
          onClose={() => setSnackbar({ open: false, error: false, message: "" })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

// ── Reusable bits ───────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon }) => {
  const icons = {
    building: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" /></svg>
    ),
    shield: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
    ),
    check: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
    ),
    clock: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    ),
  };
  return (
    <div className="bg-white rounded-2xl border border-blue-secondery shadow-sm px-4 py-3.5 flex items-center gap-3">
      <span className="w-9 h-9 rounded-xl bg-blue-secondery-2 text-blue-primary flex items-center justify-center">
        {icons[icon]}
      </span>
      <div>
        <p className="text-[10px] uppercase tracking-wider font-bold text-gray-primary">{label}</p>
        <p className="text-xl font-bold text-slate-800 leading-tight">{value}</p>
      </div>
    </div>
  );
};

const DetailRow = ({ icon, label, value, truncate }) => {
  const icons = {
    mail: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
    ),
    phone: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2.28a1 1 0 01.95.68l1.5 4.49a1 1 0 01-.5 1.21l-2.26 1.13a11 11 0 005.52 5.52l1.13-2.26a1 1 0 011.21-.5l4.49 1.5a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.72 21 3 14.28 3 6V5z" /></svg>
    ),
    map: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
    ),
  };
  return (
    <div className="flex items-start gap-2">
      <span className="w-7 h-7 rounded-lg bg-blue-secondery-2 text-blue-primary flex items-center justify-center shrink-0 mt-0.5">
        {icons[icon]}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold text-gray-primary uppercase tracking-wider">{label}</p>
        <p className={`text-[13px] text-slate-700 ${truncate ? "truncate" : ""}`} title={value}>{value || "—"}</p>
      </div>
    </div>
  );
};

export default ManageTenants;
